import { pname, fd, queueScrollToEv } from "../utils";
import { CT, RAD, FS, Z } from "../lib/tokens";
import { useContext } from "react";
import { PaneOpenContext, paneRungFor, useAbovePane } from "../lib/paneStacking"; // 205 §6 — Alt+F over an open pane; §5 — and it owns the Escape there
import { resolveMatter } from "../lib/matterLabel"; // 191 §C
import { SearchCheck, X } from "lucide-react";

// BRIEF 108 §4 — the Settings section's index: card titles from the declared
// registry, taxonomy KIND labels from the panel that renders them.
import { SETTINGS_CARDS, type SettingsCardGate } from "../lib/settingsSearchIndex";
import { KIND_LABELS } from "../views/SettingsTaxonomies";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

export const SearchPopover = ({
  sDlgQ,
  setSDlgQ,
  setSearchDlg,
  searchDlgInputRef,
  sDlgFilters,
  setSDlgFilters,
  sDlgMulti,
  setSDlgMulti,
  sDlgMore,
  setSDlgMore,
  currentUser,
  _appIsAdmin,
  _appPerms,
  openSettingsCard,
  cases,
  collectiveCases,
  clients,
  opponents,
  opponentLawyers,
  experts,
  collaborators,
  jurisdictions,
  agendaEvents,
  tasks,
  timeEntries,
  chatMsgs,
  evtTypes,
  nav,
  setDetailId,
  tryNavCase,
  t,
}: any) => {
            /* 205 §6 — the one hook this component has, at the top of its body:
               is a full-screen pane open behind Alt+F? App provides it
               (`PaneOpenContext`, 204 §1) and this component renders inside that
               provider, so the answer is a read and not a new prop. */
            const paneOpen = useContext(PaneOpenContext);
            /* 205 §5 — AND WHILE IT IS LIFTED IT OWNS THE ESCAPE. The chain's
               very FIRST arm closes this dialog (`if (searchDlg) …`,
               hooks/popoverChrome.ts), but the chain is a `window` listener and a
               pane's is on `document`, which fires first — so Alt+F over an open
               pane used to close BOTH on one press. Registering here is what makes
               the pane stand down. */
            useAbovePane(paneOpen);
            const sq = sDlgQ.trim().toLowerCase();
            /* 174 §6.6 — `_appPerms.viewer?.role?.firmWideVisibility === true`
               stood here. The field was DROPPED in Phase 24 and replaced by
               `FirmSettings.surfaceScopes`, so the expression was
               `undefined === true` — always false, and this dialog took the
               restrictive branch permanently.

               THE REPAIR IS THE SAME AS THE AGENDA'S, and for the same measured
               reason: `routes/tasks.js:214` already runs every task row through
               `buildRowGate(viewer, 'caseView.tasks.view', { caselessFirmWide })`,
               and `lib/rowScope.js:98` resolves THAT gate from
               `getVisibleCaseIds(viewer, surface)` — the surface scopes
               themselves. The `tasks` array this dialog searches is already
               exactly what the viewer may see, so the second assignee test was
               hiding rows the server had decided for. It is gone; the flag it
               read is gone with it. */
            const canSeeFin =
              _appIsAdmin || _appPerms.can("caseView.financials.view");
            const canSeeAll = true; /* M+2b: server-filtered */
            const myCases = canSeeAll
              ? cases
              : cases.filter((c: any) =>
                  (c.caseCollaborators || []).some(
                    (x: any) => x.coId === currentUser,
                  ),
                );
            const match = (item: any, fields: any) =>
              fields.some((f: any) => {
                const v = item[f];
                return v && String(v).toLowerCase().includes(sq);
              });
            const lim = (k: any) => (sDlgMore[k] ? 50 : 10);
            const anyFilt = Object.values(sDlgFilters).some((v) => v);
            const showSec = (k: any) => !anyFilt || !!sDlgFilters[k];
            const SECTIONS = [
              {
                k: "ca",
                label: t("nav.cases"),
                show: showSec("ca"),
                items:
                  sq.length < 2
                    ? []
                    : myCases.filter(
                        (c: any) =>
                          match(c, ["ref", "title", "ctype", "desc"]) ||
                          (jurisdictions.find((j: any) => j.id === c.juId)?.name || "")
                            .toLowerCase()
                            .includes(sq),
                      ),
                render: (i: any) => {
                  const j = jurisdictions.find((x: any) => x.id === i.juId);
                  return {
                    name: i.ref + " — " + i.title,
                    tag: i.ctype || null,
                    detail: j ? j.name : "",
                    nav: () => {
                      nav("ca", i.id);
                      setSearchDlg(false);
                    },
                  };
                },
              },
              {
                k: "cc",
                label: t("nav.collective_cases"),
                show: showSec("cc"),
                items:
                  sq.length < 2
                    ? []
                    : collectiveCases.filter((c: any) =>
                        match(c, ["ref", "title", "ctype", "desc"]),
                      ),
                render: (i: any) => ({
                  name: i.ref + " — " + i.title,
                  detail: i.ctype || "",
                  nav: () => {
                    nav("cc");
                    setTimeout(() => setDetailId?.(i.id), 50);
                    setSearchDlg(false);
                  },
                }),
              },
              {
                k: "cl",
                label: t("nav.clients"),
                show: showSec("cl"),
                items:
                  sq.length < 2
                    ? []
                    : clients.filter((c: any) =>
                        match(c, [
                          "firstName",
                          "lastName",
                          "companyName",
                          "email",
                          "phone",
                          "vat",
                          "nationalRegisterId",
                        ]),
                      ),
                render: (i: any) => ({
                  name: pname(i),
                  tag: i.type || null,
                  detail: [i.email, i.phone].filter(Boolean).join(" · "),
                  nav: () => {
                    nav("cl", i.id);
                    setSearchDlg(false);
                  },
                }),
              },
              {
                k: "op",
                label: t("nav.opponents"),
                show: showSec("op"),
                items:
                  sq.length < 2
                    ? []
                    : opponents.filter((c: any) =>
                        match(c, [
                          "firstName",
                          "lastName",
                          "companyName",
                          "contact",
                        ]),
                      ),
                render: (i: any) => ({
                  name: pname(i),
                  detail: i.contact || "",
                  nav: () => {
                    nav("op", i.id);
                    setSearchDlg(false);
                  },
                }),
              },
              {
                k: "ocu",
                label: t("nav.opponent_counsel"),
                show: showSec("ocu"),
                items:
                  sq.length < 2
                    ? []
                    : opponentLawyers.filter((c: any) =>
                        match(c, [
                          "firstName",
                          "lastName",
                          "firm",
                          "email",
                          "bar",
                        ]),
                      ),
                render: (i: any) => ({
                  name: pname(i),
                  tag: i.firm || null,
                  detail: i.email || "",
                  nav: () => {
                    nav("ocu", i.id);
                    setSearchDlg(false);
                  },
                }),
              },
              {
                k: "ex",
                label: t("nav.experts"),
                show: showSec("ex"),
                items:
                  sq.length < 2
                    ? []
                    : experts.filter((c: any) =>
                        match(c, [
                          "firstName",
                          "lastName",
                          "spec",
                          "office",
                          "email",
                        ]),
                      ),
                render: (i: any) => ({
                  name: pname(i),
                  tag: i.spec || null,
                  detail: i.office || "",
                  nav: () => {
                    nav("ex", i.id);
                    setSearchDlg(false);
                  },
                }),
              },
              {
                k: "co",
                label: t("nav.collaborators"),
                show: showSec("co"),
                items:
                  sq.length < 2
                    ? []
                    : collaborators.filter((c: any) =>
                        match(c, ["firstName", "lastName", "role", "email"]),
                      ),
                render: (i: any) => ({
                  name: pname(i),
                  tag: i.role || null,
                  detail: i.email || "",
                  nav: () => {
                    nav("co2", i.id);
                    setSearchDlg(false);
                  },
                }),
              },
              {
                k: "ju",
                label: t("nav.jurisdictions"),
                show: showSec("ju"),
                items:
                  sq.length < 2
                    ? []
                    : jurisdictions.filter((c: any) => match(c, ["name", "type", "chamber"])),
                render: (i: any) => ({
                  name: i.name,
                  tag: i.type || null,
                  detail: i.chamber || "",
                  nav: () => {
                    nav("ju", i.id);
                    setSearchDlg(false);
                  },
                }),
              },
              {
                k: "ag",
                label: t("search.agenda_events"),
                show: showSec("ag"),
                items:
                  sq.length < 2
                    ? []
                    : agendaEvents.filter(
                        (e: any) =>
                          match(e, ["title", "type", "location", "notes"]) ||
                          /* 191 §C — a match by the matter's title, a
                             collective case's included. */
                          (
                            resolveMatter({ caseId: e.caId, ccId: e.ccId }, cases, collectiveCases)?.title || ""
                          )
                            .toLowerCase()
                            .includes(sq),
                      ),
                render: (i: any) => {
                  /* 191 §C — the matter through the one resolver; the KIND for
                     the jump is the row's own id (the resolver answers the row). */
                  const cs2 = resolveMatter({ caseId: i.caId, ccId: i.ccId }, cases, collectiveCases);
                  return {
                    name: i.title,
                    tag: i.type || null,
                    detail: [fd(i.date), i.time, cs2?.ref]
                      .filter(Boolean)
                      .join(" · "),
                    nav: () => {
                      if (cs2) {
                        queueScrollToEv(i.id);
                        nav(i.caId != null ? "ca" : "cc", cs2.id);
                      } else nav("ag");
                      setSearchDlg(false);
                    },
                  };
                },
              },
              {
                k: "tk",
                label: t("search.tasks"),
                show: showSec("tk"),
                items:
                  sq.length < 2
                    ? []
                    : tasks.filter((t: any) => match(t, ["title", "notes"])),
                render: (i: any) => {
                  const cs2 = resolveMatter(i, cases, collectiveCases); // 191 §C
                  return {
                    name: i.title,
                    tag: i.status || null,
                    detail: [cs2?.ref, i.dueDate ? fd(i.dueDate) : null]
                      .filter(Boolean)
                      .join(" · "),
                    nav: () => {
                      if (cs2) nav(i.caseId != null ? "ca" : "cc", cs2.id);
                      // C-1 — delegated/cc rows: navigate when visible,
                      // toast when not (the dialog used to just close).
                      else if (i.caseId != null || i.ccId != null)
                        tryNavCase(
                          i.caseId != null ? "ca" : "cc",
                          i.caseId != null ? i.caseId : i.ccId,
                        );
                      setSearchDlg(false);
                    },
                  };
                },
              },
              {
                k: "te",
                label: t("search.time_entries"),
                show: showSec("te") && canSeeFin,
                items:
                  sq.length < 2
                    ? []
                    : timeEntries.filter(
                        (e: any) =>
                          match(e, ["desc"]) ||
                          (collaborators.find((c: any) => c.id === e.coId) &&
                            pname(collaborators.find((c: any) => c.id === e.coId))
                              .toLowerCase()
                              .includes(sq)),
                      ),
                render: (i: any) => {
                  const cs2 = resolveMatter({ caseId: i.caId, ccId: i.ccId }, cases, collectiveCases); // 191 §C
                  const c2 = collaborators.find((c: any) => c.id === i.coId);
                  return {
                    name:
                      i.desc ||
                      (c2 ? pname(c2) : t("search.time_entry_fallback")),
                    detail: [fd(i.date), c2 ? pname(c2) : null, cs2?.ref]
                      .filter(Boolean)
                      .join(" · "),
                    nav: () => {
                      if (cs2) nav(i.caId != null ? "ca" : "cc", cs2.id);
                      // C-1 — own time entries can ride off-scope cases/ccs.
                      else if (i.caId != null || i.ccId != null)
                        tryNavCase(
                          i.caId != null ? "ca" : "cc",
                          i.caId != null ? i.caId : i.ccId,
                        );
                      setSearchDlg(false);
                    },
                  };
                },
              },
              {
                k: "chat",
                label: t("search.chat_messages"),
                show: showSec("chat"),
                items:
                  sq.length < 2
                    ? []
                    : (() => {
                        const results: any[] = [];
                        Object.entries(chatMsgs).forEach(([cid, msgs]: any) => {
                          (msgs || []).forEach((m: any) => {
                            if (m.text && m.text.toLowerCase().includes(sq)) {
                              const cs2 = cases.find((c: any) => c.id === +cid);
                              // C-1 — keep the RAW key: cc chats live under
                              // "cc:<id>" and +cid coerces those to NaN.
                              results.push({
                                ...m,
                                _cid: +cid,
                                _cidKey: cid,
                                _cs: cs2,
                              });
                            }
                          });
                        });
                        return results.slice(0, 50);
                      })(),
                render: (i: any) => {
                  const a = collaborators.find((c: any) => c.id === i.author);
                  return {
                    /* BRIEF 102 §1a — the cut goes. This looks like §1a's
                       second branch (a chat snippet) and is not: the sink is
                       bounded and already ellipsises — `r.name` renders at
                       :760 with overflow / ellipsis / nowrap inside a
                       `flex: 1, minWidth: 0, overflow: hidden` column, so §1c's
                       second step is already satisfied and the 60-char cut
                       could only fire before the CSS could. In a wide popover
                       the reader now gets more of the message, which is what
                       "adaptive" means here. */
                    name: (a ? pname(a) : "?") + ": " + i.text,
                    detail: [
                      i._cs?.ref,
                      new Date(i.ts).toLocaleDateString("fr-BE"),
                    ]
                      .filter(Boolean)
                      .join(" · "),
                    nav: () => {
                      if (i._cs) nav("ca", i._cs.id);
                      /* C-1 — cc-keyed ("cc:<id>") and off-scope case
                         results: C85 guard (same key parse as C104's
                         mention path) instead of silently closing. */
                      else if (String(i._cidKey).startsWith("cc:"))
                        tryNavCase("cc", +String(i._cidKey).slice(3));
                      else if (i._cidKey != null)
                        tryNavCase("ca", +i._cidKey);
                      setSearchDlg(false);
                    },
                  };
                },
              },
              {
                k: "doc",
                label: t("search.documents"),
                show: showSec("doc"),
                items:
                  sq.length < 2
                    ? []
                    : (() => {
                        const results: any[] = [];
                        myCases.forEach((c: any) => {
                          (c.docs || []).forEach((d: any) => {
                            if (d.name && d.name.toLowerCase().includes(sq))
                              results.push({ ...d, _cs: c });
                          });
                        });
                        return results;
                      })(),
                render: (i: any) => ({
                  name: i.name,
                  detail: [i._cs?.ref, fd(i.date), i.status]
                    .filter(Boolean)
                    .join(" · "),
                  nav: () => {
                    if (i._cs) nav("ca", i._cs.id);
                    setSearchDlg(false);
                  },
                }),
              },
              /* BRIEF 108 §4 — Settings joins the search: the cards' own
                 titles plus the taxonomy KIND labels, each result landing on
                 its card (openSettingsCard → section change + one-shot focus
                 hint). §4c — the gate is PER ITEM, transcribed from each
                 card's mount condition into the registry and evaluated here
                 with the dialog's own inputs: admin-gated cards need
                 _appIsAdmin, key-gated cards mirror SettingsView's sg() —
                 !hidden(key) && (admin || can(key)) — and 'always' cards are
                 the ungrantable personal set. Kind labels ride the Taxonomies
                 card's own key. A non-admin therefore only ever sees results
                 whose card would actually render for them. */
              {
                k: "settings",
                label: t("nav.settings", { fallback: "Settings" }),
                show: showSec("settings"),
                items:
                  sq.length < 2
                    ? []
                    : (() => {
                        const gateOk = (g: SettingsCardGate) =>
                          g.kind === "always"
                            ? true
                            : g.kind === "admin"
                              ? _appIsAdmin
                              : !_appPerms.hidden(g.key) &&
                                (_appIsAdmin || _appPerms.can(g.key));
                        type SettingsHit = { _sPane: string; _sName: string; _sDetail?: string };
                        const out: SettingsHit[] = [];
                        for (const c of SETTINGS_CARDS) {
                          if (!gateOk(c.gate)) continue;
                          const name = t(c.labelKey, { fallback: c.fallback });
                          if (String(name).toLowerCase().includes(sq))
                            out.push({ _sPane: c.pane, _sName: name });
                        }
                        if (
                          gateOk({ kind: "key", key: "settings.customLists.taxonomies" })
                        ) {
                          for (const [, label] of KIND_LABELS) {
                            if (String(label).toLowerCase().includes(sq))
                              out.push({
                                _sPane: "taxonomies",
                                _sName: label,
                                _sDetail: t("taxonomies.title", {
                                  fallback: "Taxonomies",
                                }),
                              });
                          }
                        }
                        return out;
                      })(),
                render: (i: { _sPane: string; _sName: string; _sDetail?: string }) => ({
                  name: i._sName,
                  detail: i._sDetail || t("nav.settings", { fallback: "Settings" }),
                  nav: () => {
                    openSettingsCard?.(i._sPane);
                    setSearchDlg(false);
                  },
                }),
              },
            ]
              // P21-zero — the search dialog had NO visibility term at all:
              // `show` was purely the chip toggle, so every entity kept its
              // chip and kept matching rows however the firm's product was
              // shaped. The predicate lands HERE rather than inside showSec()
              // because SECTIONS is the one place this dialog decides what it
              // is made of — the chip row, the result groups and hasResults
              // all read it, so a hidden entity loses all three at once
              // instead of showing a chip that can only ever find nothing.
              .filter((s) => !_appPerms.hiddenSurface(s.k));
            const hasResults = SECTIONS.some(
              (s) => s.show && s.items.length > 0,
            );
            return (
              /* 188 §A.3 — NOT A MODAL, LEFT DELIBERATELY. `modal-overlay` is
                 borrowed for its backdrop and its z-index only; what it frames
                 is `.PopPanel tall`, not a `.modal` box, so ModalLayout (the
                 modal shell, S3b) does not apply here. A later pass reading the
                 class must not convert this. */
              <div
                className="modal-overlay"
                /* 205 §6 — ALT+F OVER AN OPEN PANE. Censused: this is a fixed
                   `.modal-overlay` at `Z.popoverBackdrop` (200), deliberately NOT a
                   modal (the note above), and it renders inside App's
                   `PaneOpenContext.Provider` — so it painted under the pane's 9999
                   and 204 §1's rule, which lives in `ModalLayout`, never reached it.
                   It takes the SAME rung a base dialog takes there, through the same
                   helper rather than a copy of its figure: `paneRungFor(null)` is
                   `Z.paneModal`. Inert with no pane open. */
                style={{ zIndex: paneOpen ? paneRungFor(null) : Z.popoverBackdrop }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) setSearchDlg(false);
                }}
              >
                {/* Stage-3 C24 — shared centered popover chrome (.PopPanel);
                    the .tall variant keeps a fixed height so results can
                    grow/shrink without the panel jumping. */}
                <div className="PopPanel tall">
                  <div
                    style={{
                      padding: "12px 14px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <SearchCheck
                      size={16}
                      style={{ color: "var(--ac)", flexShrink: 0 }}
                    />
                    <input
                      ref={searchDlgInputRef}
                      autoFocus
                      style={{
                        flex: 1,
                        background: "var(--bi)",
                        border: "1px solid var(--br)",
                        borderRadius: RAD.r6,
                        padding: "7px 10px",
                        color: "var(--tp)",
                        fontSize: FS.s12,
                        outline: "none",
                        fontFamily: "inherit",
                      }}
                      placeholder={t("search.placeholder")}
                      value={sDlgQ}
                      onChange={(e) => setSDlgQ(e.target.value)}
                      onKeyDown={(e) => {
                        /* 206E (§1.3's census, measured) — THE BOX CLOSES THE
                           DIALOG AND THE KEY STOPS HERE. Closing inside React's
                           root listener is a discrete update, and React flushes
                           its passive effects at once — so `useAbovePane`'s
                           cleanup (above) had unregistered this dialog BEFORE
                           the key bubbled on to `document`, where the pane's
                           listener then found nothing above it and closed too:
                           one Escape, both gone (Chromium and WebKit). Stopped
                           here, the key never reaches `document` or the hotkey
                           chain's `window` (205 §5 measured the same stop for
                           MenuSelect); the chain's own arm closes this dialog
                           when the focus is anywhere else in it. */
                        if (e.key === "Escape") {
                          e.stopPropagation();
                          setSearchDlg(false);
                        }
                      }}
                    />
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--tm)",
                        cursor: "pointer",
                        padding: 4,
                      }}
                      onClick={() => setSearchDlg(false)}
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div
                    style={{
                      padding: "0 14px 8px",
                      display: "flex",
                      gap: 3,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {SECTIONS.map((s) =>
                      s.k === "te" && !canSeeFin ? null : (
                        <button
                          key={s.k}
                          style={{
                            fontSize: FS.s9,
                            padding: "2px 8px",
                            borderRadius: RAD.r10,
                            border: "none",
                            background: sDlgFilters[s.k]
                              ? "rgba(74,124,255,.2)"
                              : "transparent",
                            color: sDlgFilters[s.k] ? "var(--ac)" : "var(--ts)",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            transition: "all .15s",
                            fontWeight: sDlgFilters[s.k] ? 600 : 400,
                          }}
                          onClick={() => {
                            if (sDlgMulti) {
                              setSDlgFilters((p: any) => ({ ...p, [s.k]: !p[s.k] }));
                            } else {
                              setSDlgFilters((p: any) => {
                                const wasOn = p[s.k];
                                return wasOn ? {} : { [s.k]: true };
                              });
                            }
                          }}
                        >
                          {s.label}
                        </button>
                      ),
                    )}
                    <button
                      style={{
                        fontSize: FS.s8,
                        padding: "2px 6px",
                        borderRadius: RAD.r10,
                        border: sDlgMulti
                          ? "1px solid var(--ag)"
                          : "1px solid var(--br)",
                        background: sDlgMulti ? "var(--as)" : "none",
                        color: sDlgMulti ? "var(--ac)" : "var(--tm)",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        marginLeft: 4,
                      }}
                      onClick={() => {
                        setSDlgMulti((v: any) => !v);
                        setSDlgFilters({});
                      }}
                    >
                      {t("agenda.btn.multi")}
                    </button>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      overflowY: "auto",
                      padding: "0 14px 12px",
                    }}
                  >
                    {sq.length < 2 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "30px 14px",
                          color: "var(--tm)",
                        }}
                      >
                        <SearchCheck
                          size={32}
                          style={{ opacity: 0.15, marginBottom: 8 }}
                        />
                        <p style={{ fontSize: FS.s11 }}>{t("search.type_min")}</p>
                      </div>
                    )}
                    {sq.length >= 2 && !hasResults && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "30px 14px",
                          color: "var(--tm)",
                        }}
                      >
                        <p style={{ fontSize: FS.s11 }}>
                          {t("search.no_results", { q: sDlgQ })}
                        </p>
                      </div>
                    )}
                    {SECTIONS.filter((s) => s.show && s.items.length > 0).map(
                      (s) => (
                        <div key={s.k} style={{ marginBottom: 14 }}>
                          <div
                            style={{
                              fontSize: FS.s9,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: ".4px",
                              color: "var(--tm)",
                              padding: "6px 0 4px",
                              borderBottom: "1px solid var(--br)",
                              marginBottom: 4,
                            }}
                          >
                            {s.label}{" "}
                            <span style={{ fontWeight: 400 }}>
                              ({s.items.length})
                            </span>
                          </div>
                          {s.items.slice(0, lim(s.k)).map((item: any, idx: any) => {
                            const r: any = s.render(item);
                            const evtT =
                              s.k === "ag" && r.tag
                                ? evtTypes.find((x: any) => x.code === r.tag)
                                : null;
                            return (
                              <div
                                key={item.id || idx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0,
                                  padding: "4px 2px",
                                  borderRadius: RAD.r5,
                                  cursor: "pointer",
                                  transition: "background .12s",
                                }}
                                onMouseEnter={(e2) =>
                                  (e2.currentTarget.style.background =
                                    "var(--bh)")
                                }
                                onMouseLeave={(e2) =>
                                  (e2.currentTarget.style.background = "none")
                                }
                                onClick={r.nav}
                              >
                                <span
                                  style={{
                                    width: 28,
                                    fontSize: FS.s7_5,
                                    fontWeight: 700,
                                    color: "var(--ac)",
                                    textTransform: "uppercase",
                                    flexShrink: 0,
                                    fontFamily: "'JetBrains Mono',monospace",
                                    textAlign: "center",
                                  }}
                                >
                                  {s.k}
                                </span>
                                <span
                                  style={{
                                    width: 52,
                                    flexShrink: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {evtT ? (
                                    <span
                                      style={{
                                        fontSize: FS.s8,
                                        padding: "1px 4px",
                                        borderRadius: RAD.r3,
                                        background:
                                          "rgba(" +
                                          parseInt(
                                            (evtT.color || CT.c_8892a4).slice(
                                              1,
                                              3,
                                            ),
                                            16,
                                          ) +
                                          "," +
                                          parseInt(
                                            (evtT.color || CT.c_8892a4).slice(
                                              3,
                                              5,
                                            ),
                                            16,
                                          ) +
                                          "," +
                                          parseInt(
                                            (evtT.color || CT.c_8892a4).slice(
                                              5,
                                              7,
                                            ),
                                            16,
                                          ) +
                                          ",.18)",
                                        color: evtT.color,
                                        fontWeight: 600,
                                        fontFamily:
                                          "'JetBrains Mono',monospace",
                                      }}
                                    >
                                      {r.tag}
                                    </span>
                                  ) : r.tag ? (
                                    <span
                                      style={{
                                        fontSize: FS.s8,
                                        color: "var(--ts)",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {r.tag}
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        fontSize: FS.s8,
                                        color: "var(--tm)",
                                      }}
                                    >
                                      —
                                    </span>
                                  )}
                                </span>
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: FS.s10_5,
                                      color: "var(--tp)",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {r.name}
                                  </div>
                                  {r.detail && (
                                    <div
                                      style={{
                                        fontSize: FS.s8_5,
                                        color: "var(--tm)",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {r.detail}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {s.items.length > lim(s.k) && (
                            <button
                              style={{
                                fontSize: FS.s9,
                                color: "var(--ac)",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px 0",
                                fontFamily: "inherit",
                              }}
                              onClick={() =>
                                setSDlgMore((p: any) => ({ ...p, [s.k]: true }))
                              }
                            >
                              {t("search.show_all_n", withCount(s.items.length, "n", { fallback: "Show all {n} results" }))}
                            </button>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            );
};
